import React from "react";
import { RPCRequests, Message } from "src/modules/communication-protobuf";
import debug from "src/modules/debug-logger";
import { getVideoSignedUrl } from "src/modules/activity-log/ajax/cloud";

import normalizeEvent from "../../../activity-log/normalizeEvent";

const WAIT_FOR_RECORDING_DID_FINISH_MESSAGE_TIMEOUT = 8000;

export default function useRecorder({
  dispatch,
  latestState,
  rpc,
  messenger,
  peerJid
}) {
  const waitForMessageTimeoutIdRef = React.useRef(null);

  const startRecording = React.useCallback(async () => {
    if (latestState.current.recorder.status !== "initial") return;
    dispatch({ type: "START_RECORDING_REQUEST" });
    const request = RPCRequests.StartVideoRecording.create();
    try {
      const { result, recordingLimit } = await rpc.call(request, peerJid);

      if (latestState.current.recorder.status !== "waitingForResponse") return;
      if (result === "ALREADY_RUNNING") {
        throw new Error(
          "StartVideoRecordingRPCResponse result === ALREADY_RUNNING"
        );
      }
      dispatch({
        type: "START_RECORDING_SUCCESS",
        payload: { recordingLimit }
      });
    } catch (error) {
      debug.activityLog("startRecording failed:", error);
      dispatch({ type: "START_RECORDING_FAILURE" });
    }
  }, [rpc, peerJid, dispatch, latestState]);

  const onRecordingDidFinish = React.useCallback(
    async ({ event, thumbnailData }) => {
      normalizeEvent(event);
      const blob = new Blob([thumbnailData]);
      const dataUrl = URL.createObjectURL(blob);
      dispatch({
        type: "RECORDING_DID_FINISH",
        payload: {
          capturedEvent: {
            ...event,
            videoThumbnailSrc: dataUrl
          }
        }
      });
      try {
        const signedUrl = await getVideoSignedUrl(event.fileName.value);
        if (latestState.current.capturedEvent?.id !== event.id) return;
        dispatch({
          type: "GET_VIDEO_SRC_SUCCESS",
          payload: { signedUrl, eventId: event.id }
        });
      } catch (error) {
        debug.activityLog("getVideoSrc failed:", error);
        dispatch({ type: "RECORDING_FAILED" });
      }
    },
    [dispatch, latestState]
  );

  const stopRecording = React.useCallback(async () => {
    if (latestState.current.recorder.status !== "recording") return;
    dispatch({ type: "STOP_RECORDING_REQUEST" });
    const request = RPCRequests.StopVideoRecording.create();
    try {
      const { result, video } = await rpc.call(request, peerJid, {
        timeout: WAIT_FOR_RECORDING_DID_FINISH_MESSAGE_TIMEOUT
      });
      if (result === "FAILURE") {
        throw new Error("StopVideoRecordingRPCResponse result === FAILURE");
      }
      onRecordingDidFinish(video);
    } catch (error) {
      debug.activityLog("stopRecording failed:", error);
      dispatch({ type: "STOP_RECORDING_FAILURE" });
    }
  }, [rpc, peerJid, onRecordingDidFinish, dispatch, latestState]);

  const getRecordingProgress = React.useCallback(() => {
    const currentDuration =
      Date.now() - latestState.current.recorder.recordingStartTime;
    return currentDuration / latestState.current.recorder.recordingLimit;
  }, [latestState]);

  const setWaitForMessageTimeout = React.useCallback(
    (recordingLimit) => {
      waitForMessageTimeoutIdRef.current = setTimeout(() => {
        debug.activityLog(
          "Recording failed. Waiting for response timeout reached."
        );
        dispatch({ type: "RECORDING_FAILED" });
      }, WAIT_FOR_RECORDING_DID_FINISH_MESSAGE_TIMEOUT);
    },
    [dispatch]
  );

  const onRecordingLimitReached = React.useCallback(() => {
    setWaitForMessageTimeout();
    if (latestState.current.recorder.status === "recording") {
      debug.activityLog(
        "Reached recording limit, before VideoRecordingDidFinishMessage"
      );
      dispatch({ type: "REACHED_RECORDING_LIMIT" });
    }
  }, [dispatch, latestState, setWaitForMessageTimeout]);

  React.useEffect(() => {
    function handleVideoRecordingDidFinishMessage({ result, video }, from) {
      clearTimeout(waitForMessageTimeoutIdRef.current);
      if (from !== peerJid) return;
      if (
        latestState.current.recorder.status === "waitingForThumbnail" ||
        latestState.current.recorder.status === "recording"
      ) {
        if (result === "FAILURE") {
          dispatch({ type: "RECORDING_FAILED" });
          return;
        }
        onRecordingDidFinish(video);
      }
    }

    messenger.on(
      Message.VideoRecordingDidFinishMessage.name,
      handleVideoRecordingDidFinishMessage
    );
    return () => {
      messenger.off(
        Message.VideoRecordingDidFinishMessage.name,
        handleVideoRecordingDidFinishMessage
      );
    };
  }, [dispatch, messenger, peerJid, onRecordingDidFinish, latestState]);

  React.useEffect(
    () => () => clearTimeout(waitForMessageTimeoutIdRef.current),
    []
  );

  return React.useMemo(
    () => ({
      startRecording,
      stopRecording,
      getRecordingProgress,
      onRecordingLimitReached
    }),
    [
      startRecording,
      stopRecording,
      getRecordingProgress,
      onRecordingLimitReached
    ]
  );
}
